/**
 * Reference documents section for an Application (Slice 2 of #857,
 * ie #860). Renders inside the per-Application detail panel on
 * `Applications.tsx` — see the dialog design in the PR body for the
 * agreed Option C hybrid layout: compact list, drag-drop on the
 * whole panel, explicit Upload button.
 *
 * Slot is between the Application Context block (summary / domain /
 * usage-context) and the Variants list. Reference docs are app-level
 * by design (see #860): one upload covers every variant + every
 * template under the same app, so the section lives alongside other
 * app-level config rather than per-variant.
 *
 * Responsibilities:
 *
 *   - List existing docs with metadata (filename, size, uploaded
 *     date, uploader, remove button). Click-to-preview not in v1.
 *   - File picker via a hidden `<input type="file">` (with `multiple`)
 *     triggered by the Upload button.
 *   - Drag-drop onto the panel — multi-file accepted; the dragged
 *     border thickens + tints to make the drop affordance
 *     unambiguous.
 *   - Per-file 5 MB cap enforced server-side; the UI surfaces the
 *     server's 400 error if the user manages to violate it from a
 *     non-standard client. (The accept attribute on the input
 *     restricts the picker to .txt / .md / .docx / .pdf / .feature;
 *     drag-drop has no equivalent so the server is the authoritative
 *     gate. Machine-readable PDFs only — scanned / image-only PDFs
 *     surface a server-side `empty-extraction` 400 since the OCR
 *     fallback is a deferred follow-up to #945.)
 *   - Per-app 25 MB aggregate original-file cap shown in the
 *     empty-state copy + populated footer (`12 MB of 25 MB total`).
 *     A secondary 500 KB extracted-text cap is enforced server-
 *     side but not surfaced in the UI (less intuitive than file
 *     size; surfaces only on rejection).
 *
 * Multi-file uploads are serialized one-at-a-time (sequential
 * `await`s rather than `Promise.all`) so the server's atomic cap-
 * check sees the post-state of each upload before evaluating the
 * next. Parallel uploads would either both pass the precheck and
 * one would be rejected by the `$expr` filter (silent failure
 * masking) or both succeed against the same pre-state (cap
 * violation). Sequential keeps the user-facing error surface
 * deterministic: each file's outcome is reported in order.
 *
 * Calls `onChange()` ONCE per upload batch (at the end of
 * `handleFiles`, only if at least one file succeeded — an all-fail
 * batch skips the refresh) and once per delete. The parent
 * (Applications.tsx) responds by re-fetching the app and
 * propagating the updated `referenceDocs[]` into its own state.
 * If the refresh itself fails (network/server error from the
 * parent's `loadApps`), the error is surfaced in the same error
 * panel as upload failures. The component is stateless beyond
 * ephemeral upload-in-flight + last-error.
 */
import { useCallback, useRef, useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { AlertTriangle, Link2, Loader2, RotateCw, Upload, X } from 'lucide-react'
import { Button } from '@/components/ui'
import { applicationsAPI } from '@/lib/api'
import { useConfirm } from '@/hooks/useConfirm'
import { useWorkspacePath } from '@/hooks/useWorkspacePath'
import type { DocumentKind, ReferenceDoc, TestIntent } from '@/types'
import { DocumentPartsReview, documentPartsReviewKey } from './DocumentPartsReview'
import { DocumentKindControl, countsAsNote } from './DocumentKindControl'
import {
  canCarryOn,
  failedLine,
  partsAlreadyRead,
  readingLabel,
  readProgressCount,
  retryingLine,
} from '@/lib/referenceDocReadStatus'

/** Per-app original-file aggregate cap (bytes), mirroring the backend's
 * `REFERENCE_DOC_ORIGINAL_SIZE_TOTAL_CAP`.
 *
 * It stayed a prop while the Apps page was the only caller, on the reasoning
 * that the call site should own it. With a second caller (Tests → Proposed,
 * #2158 slice 2) that reasoning inverts: two call sites each declaring 25 MB
 * is two places to get it wrong. Exported here, beside the component that
 * renders it, and still accepted as a prop so the value is overridable. */
export const REFERENCE_DOC_ORIGINAL_TOTAL_CAP_BYTES = 25 * 1024 * 1024

interface Props {
  appId: string
  /** The variant currently selected in the header, when this section
   * is showing the selected app (#2096). Carried on the row's View
   * link because Tests → Proposed renders an entry-barrier empty
   * state until an app AND a variant are both chosen — an app-only
   * link would drop the user on "pick from the header" instead of
   * the suggestions they asked to see.
   *
   * Optional: reference docs are app-level and this section renders
   * for every visible app, including ones that are not the current
   * header selection. When absent the link still carries the app,
   * and the Tests page reconciles a variant on arrival. */
  selectedVariantId?: string
  docs: ReferenceDoc[]
  /** Per-app original-file aggregate cap (bytes). Mirrors the
   * backend's `REFERENCE_DOC_ORIGINAL_SIZE_TOTAL_CAP` (25 MB).
   * This is the user-visible cap in the empty-state hint and
   * populated footer line.
   *
   * The shared default is `REFERENCE_DOC_ORIGINAL_TOTAL_CAP_BYTES`, exported
   * from this module — both call sites pass it rather than declaring their
   * own. Still a prop so a caller can override, but no longer the single
   * source it was while the Apps page was the only caller (#2158 slice 2). */
  totalOriginalCapBytes: number
  /** Invoked once per upload batch (only when at least one file
   * succeeded) and once per successful delete. The parent
   * re-fetches the app so its local copy of `docs` re-renders.
   * Rejection is caught and surfaced in the error panel — see
   * `handleFiles`. */
  onChange: () => void | Promise<void>
  /** Embedded mode (#2261): drop the intro sentence and the "Reference
   * documents" heading, because the caller already frames this as
   * "Manage sources". The Add-a-link, Upload, and document rows are
   * unchanged. */
  embedded?: boolean
  /** More buttons for the row, after Upload document. The Tests page puts
   * Import issues here, so every kind of source is added from one row. */
  moreActions?: ReactNode
  /** Shown right under the button row, above the link box and the
   * documents. The Tests page puts the issue list, or why it can't open
   * yet, here. This section knows nothing about issues. */
  belowActions?: ReactNode
}

/** The three kinds of test, in the order the control shows them, with the
 * words an operator reads (#2102 slice 2). `happy-path` is labelled
 * "Positive". */
const KIND_ORDER: readonly TestIntent[] = ['happy-path', 'negative', 'edge-case']
const KIND_LABEL: Record<TestIntent, string> = {
  'happy-path': 'Positive',
  negative: 'Negative',
  'edge-case': 'Edge case',
}
const KIND_HINT: Record<TestIntent, string> = {
  'happy-path': 'Correct values, expecting the feature to work (the happy path)',
  negative: 'Bad input, or the app is meant to refuse or show an error',
  'edge-case': 'Limits, empty states, boundary values',
}
/** The product default when a document has no override — Positive only.
 * Mirrors the backend `DEFAULT_PROPOSER_INTENTS`. */
const DEFAULT_KINDS: readonly TestIntent[] = ['happy-path']

/** Same set of kinds, order-independent. */
function sameKinds(a: readonly TestIntent[], b: readonly TestIntent[]): boolean {
  if (a.length !== b.length) return false
  const s = new Set(a)
  return b.every(k => s.has(k))
}

/** How a document's "Suggesting: …" line reads. Absent override → the
 * Positive default; an override lists its kinds and marks itself custom. */
function kindsSummary(doc: ReferenceDoc): { label: string; custom: boolean } {
  const override = doc.proposerIntents
  if (!override || override.length === 0) return { label: 'Positive', custom: false }
  return {
    label: KIND_ORDER.filter(k => override.includes(k)).map(k => KIND_LABEL[k]).join(', '),
    custom: true,
  }
}

/** Inline per-document editor for the kinds of test to suggest (#2102
 * slice 2). Local selection; "Done" persists an override, "Use default"
 * clears it back to Positive. The last box cannot be unticked — a
 * document that suggests nothing has no use (delete it instead). */
function ReferenceDocKindsEditor({
  current,
  busy,
  onSave,
  onUseDefault,
  onCancel,
}: {
  current: TestIntent[] | undefined
  busy: boolean
  onSave: (next: TestIntent[]) => void
  onUseDefault: () => void
  onCancel: () => void
}) {
  const [selected, setSelected] = useState<Set<TestIntent>>(
    () => new Set(current && current.length > 0 ? current : DEFAULT_KINDS),
  )
  const [error, setError] = useState<string | null>(null)

  function toggle(kind: TestIntent) {
    const next = new Set(selected)
    if (next.has(kind)) {
      if (next.size === 1) {
        setError('Pick at least one kind, or this document would suggest nothing.')
        return
      }
      next.delete(kind)
    } else {
      next.add(kind)
    }
    setError(null)
    setSelected(next)
  }

  return (
    <div className="mt-2 rounded-md border border-primary/35 bg-primary/[0.04] p-2.5">
      <div className="text-[11px] font-semibold">Kinds to suggest from this document</div>
      <div className="mt-2 flex flex-wrap gap-x-5 gap-y-2">
        {KIND_ORDER.map(kind => (
          <label key={kind} className="flex max-w-[220px] items-start gap-2 text-xs">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={selected.has(kind)}
              disabled={busy}
              onChange={() => toggle(kind)}
            />
            <span>
              <span className="font-medium">{KIND_LABEL[kind]}</span>
              <span className="block text-[11px] font-normal text-muted-foreground">{KIND_HINT[kind]}</span>
            </span>
          </label>
        ))}
      </div>
      {error && <p className="mt-1.5 text-[11px] text-destructive" role="alert">{error}</p>}
      <div className="mt-2 flex items-center gap-3 text-[11px]">
        <button
          type="button"
          disabled={busy}
          onClick={onUseDefault}
          className="font-medium text-primary underline hover:no-underline disabled:opacity-60"
        >
          Use default (Positive)
        </button>
        <span className="ml-auto flex items-center gap-2">
          <button
            type="button"
            disabled={busy}
            onClick={onCancel}
            className="text-muted-foreground hover:text-foreground"
          >
            Cancel
          </button>
          <Button size="sm" variant="outline" disabled={busy} onClick={() => onSave(KIND_ORDER.filter(k => selected.has(k)))}>
            Done
          </Button>
        </span>
      </div>
    </div>
  )
}

/** Compact byte formatter — KB / MB with 0 or 1 decimal. Used in
 * the per-row size and the footer total. */
function fmtBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

/** Per-row in-progress / failure pill (#945 follow-up). Pure
 * presentation: state comes from `doc.extractionStatus` directly,
 * no fetching or polling here — `Applications.tsx` owns the polling
 * loop that mutates the doc state. Visibility rules:
 *
 *   - `pending` — gray "Queued" pill. The outbox row exists; the
 *     dispatcher hasn't claimed it yet (poll interval is ~5s on the
 *     backend, so this state is usually transient).
 *   - `extracting` — yellow "Extracting…" pill with a spinner. The
 *     LLM-driven proposer is running against this doc.
 *   - `done` — the extraction result (#2096): "N tests found" with a
 *     View link into Tests → Proposed filtered to this doc, or a plain
 *     "No tests found" when it produced nothing.
 *
 *     This state used to render nothing at all, on the reasoning that
 *     finishing was signalled "by the pill disappearing AND by the
 *     Candidates list refreshing in the parent". #1827 moved that list
 *     to Tests → Proposed, so the second half of that stopped being
 *     true and the row became a dead end — it could not be told from a
 *     doc that had produced nothing.
 *   - `retrying` — amber "Retrying" pill. A try failed and the next one
 *     is already queued, so it is not a failure yet and offers no Retry;
 *     the row shows why and when beneath it, always open.
 *   - `failed` — red "Failed" pill, clickable to expand the failure
 *     reason + a Retry button. Shown only once no more tries are coming. Re-extraction is a deliberate user
 *     action (no automatic retry — we don't want to burn LLM tokens
 *     on inputs that consistently fail).
 *   - `undefined` — no pill. Legacy docs uploaded before Slice 3A
 *     pre-date the proposer; the backfill eventually flips them to
 *     terminal but transitional responses can omit the field. */
function ExtractionStatusPill({
  status,
  candidateCount,
  viewHref,
  expanded,
  onClick,
  readingLabel,
  progressCount,
}: {
  status: ReferenceDoc['extractionStatus']
  /** Suggestions currently attributed to this doc. `undefined` on
   * responses that predate the field — treated as "say nothing"
   * rather than as zero, so an older backend can't make a doc that
   * produced plenty claim it produced none. */
  candidateCount?: number
  /** Where View goes, or null when there is nowhere for it to go.
   * Tests → Proposed refuses to render without a selected variant, so
   * an account with no variant anywhere would otherwise get a link to
   * a screen telling them to pick a variant that does not exist. The
   * count still shows — that information is true and useful — only
   * the link is withheld. */
  viewHref: string | null
  expanded: boolean
  onClick?: () => void
  /** While reading: "Finding requirements…" or "Reading · 40 of 120
   * requirements". Null shows the plain "Extracting…". */
  readingLabel?: string | null
  /** While retrying a read in rounds: "40 of 120 requirements". */
  progressCount?: string | null
}) {
  if (status === 'done') {
    if (candidateCount === undefined) return null
    if (candidateCount === 0) {
      // Deliberately not styled as a failure. A glossary or a policy
      // page legitimately contains nothing testable; dressing that as
      // an error trains people to retry something that worked.
      return (
        <span className="inline-flex items-center rounded border border-border bg-background px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
          No tests found
        </span>
      )
    }
    return (
      <span className="inline-flex items-center gap-1 rounded border border-teal-600/35 bg-teal-600/10 px-1.5 py-0.5 text-[10px] font-semibold text-teal-700 dark:text-teal-400">
        {candidateCount} {candidateCount === 1 ? 'test' : 'tests'} found
        {viewHref && (
          <>
            <span aria-hidden="true">·</span>
            <Link to={viewHref} className="underline underline-offset-2 hover:no-underline">
              View
            </Link>
          </>
        )}
      </span>
    )
  }
  if (!status) return null
  if (status === 'pending') {
    return (
      <span className="inline-flex items-center gap-1 rounded border border-border bg-muted/60 px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
        Queued
      </span>
    )
  }
  if (status === 'extracting') {
    return (
      <span className="inline-flex items-center gap-1 rounded border border-amber-500/30 bg-amber-500/10 px-1.5 py-0.5 text-[10px] font-medium text-amber-600 dark:text-amber-400">
        <Loader2 className="h-2.5 w-2.5 animate-spin" />
        {readingLabel ?? 'Extracting…'}
      </span>
    )
  }
  if (status === 'retrying') {
    return (
      <span className="inline-flex items-center gap-1 rounded border border-amber-500/30 bg-amber-500/10 px-1.5 py-0.5 text-[10px] font-medium text-amber-600 dark:text-amber-400">
        <RotateCw className="h-2.5 w-2.5" />
        {progressCount ? `Retrying · ${progressCount}` : 'Retrying'}
      </span>
    )
  }
  // 'failed' — clickable pill that toggles the inline failure detail.
  return (
    <button
      type="button"
      onClick={onClick}
      aria-expanded={expanded}
      className="inline-flex items-center gap-1 rounded border border-destructive/30 bg-destructive/10 px-1.5 py-0.5 text-[10px] font-medium text-destructive hover:bg-destructive/15"
    >
      {expanded ? '▾' : '▸'} Failed
    </button>
  )
}

/** Relative date — "2 hrs ago" style. Imperfect cousin of date-fns'
 * `formatDistanceToNow`, intentionally lightweight to avoid pulling
 * the dep just for one row. Pluralizes unit suffix so "1 min ago" /
 * "2 mins ago" both read correctly. */
function fmtRelative(iso: Date | string): string {
  const date = typeof iso === 'string' ? new Date(iso) : iso
  const diffMs = Date.now() - date.getTime()
  const diffSec = Math.floor(diffMs / 1000)
  const plural = (n: number, unit: string) => `${n} ${unit}${n === 1 ? '' : 's'} ago`
  if (diffSec < 60) return 'just now'
  if (diffSec < 3600) return plural(Math.floor(diffSec / 60), 'min')
  if (diffSec < 86400) return plural(Math.floor(diffSec / 3600), 'hr')
  if (diffSec < 86400 * 7) return plural(Math.floor(diffSec / 86400), 'day')
  return date.toLocaleDateString()
}

/** Absolute date for a link doc's "Saved from this link on <date>"
 * line — e.g. "Jul 20, 2026". Unlike the relative uploaded-at stamp,
 * the saved-from date reads better absolute (the operator wants to know
 * when the snapshot was taken, not "3 days ago"). */
function fmtSavedDate(iso: Date | string): string {
  const date = typeof iso === 'string' ? new Date(iso) : iso
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

export function ReferenceDocsSection({
  appId,
  selectedVariantId,
  docs,
  totalOriginalCapBytes,
  onChange,
  embedded = false,
  moreActions,
  belowActions,
}: Props) {
  const wp = useWorkspacePath()
  /** Deep link into Tests → Proposed, filtered to one document
   * (#2096), or `null` when there is nowhere to land.
   *
   * Carries `appId` so the Tests page aligns global scope before it
   * fetches — the same handoff the retired Applications candidates
   * link used — plus `variantId`, because that page returns an
   * entry-barrier empty state until an app AND a variant are both
   * selected.
   *
   * Returns null without a variant rather than linking anyway: an
   * application with no variants clears the global selection, so the
   * link would land on "Pick an app and variant", which is advice the
   * user cannot act on when the reason there is no variant is that
   * none exists. The row shows its count and offers no link instead.
   *
   * The variant need not belong to THIS application. The link carries
   * its own `appId`, and the Tests handoff reconciles a mismatched
   * variant by substituting the application's first one — which is why
   * the parent passes the header selection for every row rather than
   * only for the selected application. */
  const buildViewHref = useCallback(
    (docId: string): string | null => {
      if (!selectedVariantId) return null
      const params = new URLSearchParams({
        stage: 'proposed',
        appId,
        variantId: selectedVariantId,
        referenceDocId: docId,
      })
      return `${wp('/tests')}?${params.toString()}`
    },
    [wp, appId, selectedVariantId],
  )
  const inputRef = useRef<HTMLInputElement>(null)
  // #1215: gate the row-level X-button delete behind a themed confirm.
  // The previous one-click delete had no undo affordance — an accidental
  // click on a doc the operator wanted to keep cost the upload + the
  // proposer run.
  const confirm = useConfirm()
  const [uploading, setUploading] = useState(false)
  const [dragOver, setDragOver] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // Set of doc ids whose Failed pill has been clicked open. Local UI
  // state (does not survive a re-fetch resetting `docs`) — fine,
  // because (a) the user actively clicked to expand and won't be
  // confused if a poll-driven refresh briefly collapses it, and (b)
  // a failed doc that the user just clicked Retry on flips back to
  // pending immediately, at which point there's nothing to expand.
  const [expandedFailures, setExpandedFailures] = useState<ReadonlySet<string>>(
    () => new Set(),
  )
  // Set of doc ids currently being retried. The pill itself flips
  // to `pending` server-side as soon as the re-propose request lands,
  // but until the next poll refreshes the docs prop, the user sees
  // no movement. The local "retrying" badge bridges that gap and
  // also disables the Retry button to prevent double-fire.
  const [retrying, setRetrying] = useState<ReadonlySet<string>>(
    () => new Set(),
  )
  // Add-a-link composer (#1806). Toggled open by the "Add a link"
  // button; holds the pasted URL and an in-flight guard.
  const [linkComposerOpen, setLinkComposerOpen] = useState(false)
  const [linkUrl, setLinkUrl] = useState('')
  const [addingLink, setAddingLink] = useState(false)
  // Doc ids whose "Refresh from link" is in flight — disables that
  // row's Refresh, mirroring the existing Retry in-flight guard.
  const [refreshing, setRefreshing] = useState<ReadonlySet<string>>(
    () => new Set(),
  )
  // #2102 slice 2 — the per-document kinds editor. `editingKindsDocId`
  // names the one open row; `savingKinds` gates its controls in-flight.
  const [editingKindsDocId, setEditingKindsDocId] = useState<string | null>(null)
  const [savingKinds, setSavingKinds] = useState(false)
  // Doc ids whose "Counts as" choice is being saved; disables that row's
  // control until the refresh lands.
  const [savingCountsAs, setSavingCountsAs] = useState<ReadonlySet<string>>(
    () => new Set(),
  )

  /** Save what a document counts as. Always sends the request, even for
   * the option already shown as selected: see the "Counts as" control. */
  const handlePickCountsAs = useCallback(
    async (doc: ReferenceDoc, kind: DocumentKind) => {
      setError(null)
      setSavingCountsAs(prev => new Set(prev).add(doc.id))
      try {
        await applicationsAPI.setReferenceDocCountsAs(appId, doc.id, kind)
        await onChange()
      } catch (e) {
        setError(
          e instanceof Error
            ? `Couldn't save what this document counts as. ${e.message}`
            : "Couldn't save what this document counts as.",
        )
      } finally {
        setSavingCountsAs(prev => {
          const next = new Set(prev)
          next.delete(doc.id)
          return next
        })
      }
    },
    [appId, onChange],
  )

  /** Persist a document's kind override — or clear back to the default
   * when the selection is exactly Positive. No-op guards keep an unchanged
   * selection from spending a request or raising a stale nudge. */
  const handleSaveKinds = useCallback(
    async (doc: ReferenceDoc, next: TestIntent[]) => {
      setError(null)
      const nextIsDefault = next.length === 1 && next[0] === 'happy-path'
      const current = doc.proposerIntents
      if (nextIsDefault && (!current || current.length === 0)) {
        setEditingKindsDocId(null)
        return
      }
      if (current && current.length > 0 && sameKinds(current, next)) {
        setEditingKindsDocId(null)
        return
      }
      setSavingKinds(true)
      try {
        await applicationsAPI.setReferenceDocKinds(appId, doc.id, nextIsDefault ? null : next)
        setEditingKindsDocId(null)
        await onChange()
      } catch (e) {
        setError(
          e instanceof Error
            ? `Couldn't update the kinds of tests. ${e.message}`
            : "Couldn't update the kinds of tests.",
        )
      } finally {
        setSavingKinds(false)
      }
    },
    [appId, onChange],
  )

  /** Clear a document's override so it reverts to the Positive default. */
  const handleUseDefaultKinds = useCallback(
    async (doc: ReferenceDoc) => {
      setError(null)
      if (!doc.proposerIntents || doc.proposerIntents.length === 0) {
        setEditingKindsDocId(null)
        return
      }
      setSavingKinds(true)
      try {
        await applicationsAPI.setReferenceDocKinds(appId, doc.id, null)
        setEditingKindsDocId(null)
        await onChange()
      } catch (e) {
        setError(
          e instanceof Error
            ? `Couldn't update the kinds of tests. ${e.message}`
            : "Couldn't update the kinds of tests.",
        )
      } finally {
        setSavingKinds(false)
      }
    },
    [appId, onChange],
  )

  // Footer uses the user-visible original-file aggregate (sum of
  // `originalSizeBytes`) against the 25 MB cap. The extracted-text
  // aggregate is also bounded server-side but is less intuitive in
  // UI units (KB scale, can't be eyeballed from a file picker) so
  // it surfaces only on rejection.
  const totalOriginalBytes = docs.reduce((sum, d) => sum + d.originalSizeBytes, 0)

  // A ref mirrors `uploading` so the in-flight guard sees the latest
  // value inside callbacks captured at render time. The Upload button
  // already gates on the `uploading` state, but drag-drop has no such
  // affordance — a user could drop another batch while a prior one
  // is still in-flight, which (without the guard) would race the
  // sequential upload + onChange refresh and overlap error/toast
  // state. Per Copilot review on #866.
  const uploadingRef = useRef(false)

  const handleFiles = useCallback(async (files: FileList | File[]) => {
    if (uploadingRef.current) return
    setError(null)
    const list = Array.from(files)
    if (list.length === 0) return
    uploadingRef.current = true
    setUploading(true)
    // Per-file success / failure tracking. We upload sequentially
    // so the server's atomic cap-check sees the post-state of each
    // upload before evaluating the next — parallel uploads with
    // `Promise.all` would either silently mask one failure or
    // violate the cap against a shared pre-state.
    const failures: Array<{ filename: string; message: string }> = []
    let succeeded = 0
    try {
      for (const file of list) {
        try {
          await applicationsAPI.uploadReferenceDoc(appId, file)
          succeeded++
        } catch (e) {
          // Server's `message` is the user-friendly explanation
          // (per-file cap, total-text cap, total-file cap,
          // unsupported format, empty extraction). The error
          // thrown by `uploadReferenceDoc` already carries that
          // as `e.message`.
          failures.push({
            filename: file.name,
            message: e instanceof Error ? e.message : 'Upload failed',
          })
        }
      }
      if (failures.length > 0) {
        // Format as one prefix line + one indented line per failure.
        // Keeps the error legible whether 1 or N files failed.
        const prefix = list.length === 1
          ? failures[0]!.message
          : `${succeeded} of ${list.length} uploaded. Failures:\n` +
            failures.map(f => `• ${f.filename}: ${f.message}`).join('\n')
        setError(prefix)
      }
      // Refresh once at the end (not per-file) so the parent only
      // re-renders the list once. Skipping the refresh entirely when
      // every file failed avoids a no-op refetch. The refresh itself
      // can fail (parent's `loadApps` is a network call) — catch and
      // surface in the same error panel rather than letting an
      // unhandled rejection escape. Append to any existing failure
      // text so a partial-success refresh-fail batch still shows
      // both signals. Per Copilot review on #883.
      if (succeeded > 0) {
        try {
          await onChange()
        } catch (e) {
          const refreshMsg = e instanceof Error ? e.message : 'Refresh failed'
          setError(prev => prev
            ? `${prev}\n\nUploaded ${succeeded} file${succeeded === 1 ? '' : 's'} but failed to refresh: ${refreshMsg}`
            : `Uploaded ${succeeded} file${succeeded === 1 ? '' : 's'} but failed to refresh: ${refreshMsg}`,
          )
        }
      }
    } finally {
      uploadingRef.current = false
      setUploading(false)
      // Reset the input so re-uploading the same file (after a
      // failure or a delete + replay) fires the change handler
      // again — input value-change events otherwise don't refire
      // on the same selection.
      if (inputRef.current) inputRef.current.value = ''
    }
  }, [appId, onChange])

  const handleDelete = useCallback(async (doc: ReferenceDoc) => {
    const ok = await confirm({
      title: 'Delete this reference document?',
      description: (
        <>
          <strong>{doc.filename}</strong> will be removed from this app, along with the
          proposed tests it produced. Proposed tests you edited or built a test from are kept and
          marked &ldquo;document deleted&rdquo;.
        </>
      ),
      confirmLabel: 'Delete document',
      destructive: true,
    })
    if (!ok) return
    setError(null)
    try {
      await applicationsAPI.deleteReferenceDoc(appId, doc.id)
      await onChange()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Delete failed')
    }
  }, [appId, confirm, onChange])

  /** Toggle a failed-row's expanded inline detail. Functional update
   * so concurrent clicks on different rows don't clobber each other's
   * state. */
  const toggleExpanded = useCallback((docId: string) => {
    setExpandedFailures((prev) => {
      const next = new Set(prev)
      if (next.has(docId)) next.delete(docId)
      else next.add(docId)
      return next
    })
  }, [])

  /** Re-extract a single failed doc (#945 follow-up). Calls the
   * existing per-doc re-propose endpoint, which hard-deletes the
   * doc's existing proposed candidates (preserving user-edited ones)
   * and re-enqueues the proposer outbox row. The pill flips back to
   * `pending` on the next poll cycle. Local `retrying` state covers
   * the gap between the API call returning and the poll picking up
   * the new status, AND disables the button to prevent double-fire. */
  const handleRetry = useCallback(
    async (docId: string, opts: { continue?: boolean } = {}) => {
      setError(null)
      setRetrying((prev) => {
        const next = new Set(prev)
        next.add(docId)
        return next
      })
      try {
        await applicationsAPI.reproposeForDoc(appId, docId, opts)
        // Collapse the failure detail now that the user has acted
        // on it — no need to show the prior failure reason once
        // a fresh attempt is in flight.
        setExpandedFailures((prev) => {
          if (!prev.has(docId)) return prev
          const next = new Set(prev)
          next.delete(docId)
          return next
        })
        // Trigger a parent refresh so the doc's `extractionStatus`
        // immediately reflects the new 'pending' (the polling loop
        // will then continue from there).
        await onChange()
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Retry failed')
      } finally {
        setRetrying((prev) => {
          if (!prev.has(docId)) return prev
          const next = new Set(prev)
          next.delete(docId)
          return next
        })
      }
    },
    [appId, onChange],
  )

  /** Add a reference doc by link (#1806). The server reads the linked
   * page/file once and saves its text. On success we clear + close the
   * composer and refresh the parent; on failure (bad/blocked/unreadable
   * link, oversize, or an already-saved 409) the server's plain-
   * language message lands in the shared error panel. */
  const handleAddLink = useCallback(async () => {
    const url = linkUrl.trim()
    if (!url || addingLink) return
    setError(null)
    setAddingLink(true)
    try {
      await applicationsAPI.addReferenceDocLink(appId, url)
      setLinkUrl('')
      setLinkComposerOpen(false)
      await onChange()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not add that link.')
    } finally {
      setAddingLink(false)
    }
  }, [appId, linkUrl, addingLink, onChange])

  /** Refresh a link doc from its saved link (#1806). Re-reads the page
   * and updates the saved copy + date only — no suggestions regenerate,
   * no AI spend. A failed re-read leaves the prior copy intact; its
   * reason lands in the error panel. The parent refresh picks up the
   * new date and, when the content changed, the stale-suggestions
   * nudge. */
  const handleRefresh = useCallback(
    async (docId: string) => {
      if (refreshing.has(docId)) return
      setError(null)
      setRefreshing((prev) => new Set(prev).add(docId))
      try {
        await applicationsAPI.refreshReferenceDocLink(appId, docId)
        await onChange()
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Could not refresh that link.')
      } finally {
        setRefreshing((prev) => {
          if (!prev.has(docId)) return prev
          const next = new Set(prev)
          next.delete(docId)
          return next
        })
      }
    },
    [appId, refreshing, onChange],
  )

  return (
    <div className="space-y-2">
      {/* #2102 slice 2 — the kinds control moved onto each document; a
          one-line teach replaces the app-level box. Hidden when embedded
          (#2261): the "Manage sources" control already frames this. */}
      {!embedded && (
        <p className="text-[11px] text-muted-foreground">
          New documents and links suggest{' '}
          <span className="font-medium text-foreground">Positive</span> tests. Open any one to change what it suggests.
        </p>
      )}
      <div className="flex items-center justify-between">
        {!embedded && <span className="text-sm font-medium">Reference documents</span>}
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setError(null)
              setLinkComposerOpen((v) => !v)
            }}
            aria-expanded={linkComposerOpen}
          >
            <Link2 className="h-3 w-3 mr-1" />
            Add a link
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={uploading}
            onClick={() => inputRef.current?.click()}
          >
            <Upload className="h-3 w-3 mr-1" />
            {uploading ? 'Uploading…' : 'Upload document'}
          </Button>
          {moreActions}
        </div>
      </div>

      {belowActions}

      {/* Add-a-link composer (#1806). Paste a link to a web page or a
          supported file; the server reads it once and saves its text.
          Enter submits; the Add button mirrors the in-flight state. */}
      {linkComposerOpen && (
        <div className="rounded-lg border border-border bg-card p-3">
          <div className="flex items-center gap-2">
            <input
              type="url"
              inputMode="url"
              autoFocus
              value={linkUrl}
              onChange={(e) => setLinkUrl(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  void handleAddLink()
                }
              }}
              placeholder="Paste a link to a page or file"
              className="flex-1 rounded-md border border-border bg-background px-2.5 py-1.5 text-xs outline-none focus:ring-2 focus:ring-primary"
            />
            <Button
              size="sm"
              disabled={addingLink || linkUrl.trim().length === 0}
              onClick={() => void handleAddLink()}
            >
              {addingLink ? (
                <>
                  <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                  Adding…
                </>
              ) : (
                'Add'
              )}
            </Button>
          </div>
          <p className="mt-2 text-[10px] text-muted-foreground">
            We read the page once and save a copy of its text. Works with a web
            page, or a PDF, Word, Markdown, text, or .feature file at a link.
          </p>
        </div>
      )}

      {/* Hidden file input — the Upload button programmatically
          clicks this. `multiple` allows selecting several files at
          once; the OS picker honors `accept` to restrict to the
          supported formats. The server cross-checks MIME +
          extension authoritatively. */}
      <input
        ref={inputRef}
        type="file"
        multiple
        // `.feature` (Cucumber/Gherkin) is in the allowlist — no
        // standard MIME exists for it, so OS pickers fall back to
        // text/plain or octet-stream. The server's extension check
        // is the authoritative signal. Per #872 follow-up.
        // `.pdf` added in #945 — machine-readable PDFs only; the
        // server returns 400 with an `empty-extraction` message
        // for scanned / image-only PDFs.
        accept=".txt,.md,.docx,.pdf,.feature,text/plain,text/markdown,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/pdf"
        className="hidden"
        onChange={(e) => {
          if (e.target.files && e.target.files.length > 0) {
            void handleFiles(e.target.files)
          }
        }}
      />

      {/* Drop-target panel. Border styling has three states:
            - Empty + idle:  1px dashed `border-border`. Signals
                             "this is a drop zone" up-front.
            - Drag-over:     `border-2 border-primary border-dashed`
                             + tinted `bg-primary/10`. Thicker
                             stroke + brighter tint make the
                             "you're about to drop" affordance
                             unambiguous — the previous 1px primary
                             border was easy to miss on a busy
                             page.
            - Populated:     solid `border-border` — the container
                             is now a list of uploaded docs, not a
                             dropzone primary affordance (the
                             Upload button is still there for re-
                             uploads).
          Multi-file drops are supported; sequential upload via
          handleFiles guarantees the server's atomic cap-check sees
          each post-state before the next file is considered. */}
      <div
        className={`rounded-lg transition-colors ${
          dragOver
            ? 'border-2 border-primary border-dashed bg-primary/10'
            : docs.length === 0
              ? 'border border-dashed border-border bg-card'
              : 'border border-border bg-card'
        }`}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true) }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault()
          setDragOver(false)
          if (e.dataTransfer.files.length > 0) {
            void handleFiles(e.dataTransfer.files)
          }
        }}
      >
        {docs.length === 0 ? (
          /* Empty-state shape #1218 Group E. We can't use the shared
             <EmptyState> primitive here because the surrounding box is
             a drop-target with `onDragOver` / `onDrop` handlers — the
             dropzone affordance has to be the empty state itself.
             Instead we mirror the EmptyState shape inline: small title +
             prose body + dropzone hint + the "Best for" guidance. */
          <div className="p-6 text-center">
            <h4 className="text-sm font-semibold mb-1">No reference docs yet</h4>
            <p className="text-xs text-muted-foreground max-w-md mx-auto mb-3">
              Reference docs help the planner understand your domain. Upload a
              file, drop files here, or add a link to a page or file to start.
            </p>
            <div className="text-[10px] text-muted-foreground">
              .txt, .md, .docx, .pdf, .feature · up to 5 MB each · {fmtBytes(totalOriginalCapBytes)} total per app
            </div>
            {/* Concrete examples replace the prior "500 KB total per app"
                copy — the byte budget didn't help users decide whether
                to upload (the cap is enforced server-side with a clear
                error message); a hint about WHICH docs are valuable to
                the planner is more actionable.

                Ordered by planner-utility (highest first):
                  - Gherkin .feature files: each Scenario block maps
                    almost 1:1 to a candidate. Highest-value input.
                  - Manual test plans: already-enumerated tests
                    translate one-to-one.
                  - Acceptance criteria: explicit definition-of-done
                    per story.
                  - PRDs: product behavior descriptions ("the app
                    shall…").
                  - User stories: narrative behavior format.

                "Manual test plans" (not "feature specs") — "feature
                specs" evokes Gherkin .feature files for BDD-fluent
                users, and the "manual" qualifier on test plans
                disambiguates from ottotester's internal Test Plan
                entity (the runtime artifact a run executes). */}
            <div className="mt-2 text-[10px] text-muted-foreground/80">
              Best for: Gherkin .feature files, manual test plans, acceptance criteria, product requirements documents (PRDs), or user stories
            </div>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {docs.map((doc) => {
              const expanded = expandedFailures.has(doc.id)
              const isRetrying = retrying.has(doc.id)
              const isLink = doc.origin === 'link'
              const isRefreshing = refreshing.has(doc.id)
              return (
                <div key={doc.id} className="px-3 py-2 text-xs">
                  <div className="flex items-center gap-2">
                    {/* Link icon marks a doc that came from a pasted link
                        (#1806) — uploads stay iconless. */}
                    {isLink && (
                      <Link2 className="h-3.5 w-3.5 shrink-0 text-primary" aria-label="Added by link" />
                    )}
                    <span className="flex-1 truncate font-medium" title={doc.filename}>
                      {doc.filename}
                    </span>
                    <ExtractionStatusPill
                      status={doc.extractionStatus}
                      candidateCount={doc.candidateCount}
                      viewHref={buildViewHref(doc.id)}
                      expanded={expanded}
                      onClick={() => toggleExpanded(doc.id)}
                      readingLabel={readingLabel(doc)}
                      progressCount={readProgressCount(doc)}
                    />
                    <span className="text-muted-foreground tabular-nums">
                      {fmtBytes(doc.originalSizeBytes)}
                    </span>
                    {/* Uploads show a relative uploaded-at here; link docs
                        carry an absolute "Saved from this link on…" date
                        on their provenance line below instead. */}
                    {!isLink && (
                      <>
                        <span className="text-muted-foreground">·</span>
                        <span className="text-muted-foreground">
                          {fmtRelative(doc.uploadedAt)}
                        </span>
                      </>
                    )}
                    <button
                      type="button"
                      className="ml-1 text-muted-foreground hover:text-destructive"
                      title={`Remove ${doc.filename}`}
                      aria-label={`Remove ${doc.filename}`}
                      onClick={() => void handleDelete(doc)}
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>

                  {/* #2102 slice 2 — what this document suggests, with an
                      Edit affordance that opens the inline three-box editor.
                      Absent override → "Positive (default)"; an override
                      lists its kinds and marks itself custom. */}
                  {(() => {
                    const summary = kindsSummary(doc)
                    const editing = editingKindsDocId === doc.id
                    return (
                      <div className="mt-1.5">
                        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-muted-foreground">
                          <span>Suggesting</span>
                          <span className={summary.custom ? 'font-semibold text-primary' : 'font-semibold text-foreground'}>
                            {summary.label}
                          </span>
                          {summary.custom ? (
                            <span className="rounded border border-primary/40 bg-primary/10 px-1 text-[9px] font-bold uppercase tracking-wide text-primary">
                              custom
                            </span>
                          ) : (
                            <span>(default)</span>
                          )}
                          {!editing && (
                            <button
                              type="button"
                              className="font-semibold text-primary underline hover:no-underline"
                              onClick={() => setEditingKindsDocId(doc.id)}
                            >
                              Edit
                            </button>
                          )}
                        </div>
                        {editing && (
                          <ReferenceDocKindsEditor
                            current={doc.proposerIntents}
                            busy={savingKinds}
                            onSave={(next) => void handleSaveKinds(doc, next)}
                            onUseDefault={() => void handleUseDefaultKinds(doc)}
                            onCancel={() => setEditingKindsDocId(null)}
                          />
                        )}
                      </div>
                    )
                  })()}

                  {/* What the document counts as. Sits under "Suggesting",
                      as in the mockup's Sources section. */}
                  <DocumentKindControl
                    label="Counts as"
                    groupName={`What ${doc.filename} counts as`}
                    selected={doc.effectiveKind ?? 'reference'}
                    note={countsAsNote(doc)}
                    busy={savingCountsAs.has(doc.id)}
                    onPick={(kind) => {
                      // A click on the option already selected still saves
                      // when it was only a suggestion: the click is how the
                      // user confirms it. Only the user's own choice is a no-op.
                      if (kind === (doc.effectiveKind ?? 'reference') && doc.kindSource === 'user') return
                      void handlePickCountsAs(doc, kind)
                    }}
                  />

                  {/* The one-time review of what the read found, before it
                      counts. Keyed on the reviewed list so a new read's list
                      starts from its own ticks. */}
                  <DocumentPartsReview
                    key={documentPartsReviewKey(doc)}
                    appId={appId}
                    doc={doc}
                    onChange={onChange}
                  />

                  {/* Link provenance line (#1806): the saved link, when it
                      was saved, and a per-row Refresh. Refresh re-reads
                      the page and updates the saved copy + date only — no
                      suggestions regenerate, no AI spend. Disabled while
                      in flight, mirroring the Retry guard. */}
                  {isLink && (
                    <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 pl-[22px] text-[11px] text-muted-foreground">
                      {doc.sourceUrl && (
                        <a
                          href={doc.sourceUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="max-w-[340px] truncate hover:text-primary hover:underline"
                          title={doc.sourceUrl}
                        >
                          {doc.sourceUrl}
                        </a>
                      )}
                      {doc.fetchedAt && (
                        <>
                          <span>·</span>
                          <span>Saved from this link on {fmtSavedDate(doc.fetchedAt)}</span>
                        </>
                      )}
                      <span>·</span>
                      <button
                        type="button"
                        disabled={isRefreshing}
                        onClick={() => void handleRefresh(doc.id)}
                        className="inline-flex items-center gap-1 font-medium text-primary hover:underline disabled:opacity-60"
                        aria-label={`Refresh ${doc.filename} from its link`}
                      >
                        <RotateCw className={`h-3 w-3 ${isRefreshing ? 'animate-spin' : ''}`} />
                        {isRefreshing ? 'Refreshing…' : 'Refresh from link'}
                      </button>
                    </div>
                  )}

                  {/* Content-changed nudge (#1806): shown after a Refresh
                      that changed the saved text. Re-propose reuses the
                      existing per-doc action, which keeps any suggestions
                      the operator edited. */}
                  {doc.suggestionsStale && (
                    <div className="mt-1.5 ml-[22px] inline-flex flex-wrap items-center gap-x-2 gap-y-1 rounded border border-amber-500/30 bg-amber-500/10 px-2 py-1 text-[11px] text-amber-700 dark:text-amber-400">
                      <AlertTriangle className="h-3 w-3 shrink-0" />
                      <span>
                        {doc.staleReason === 'settings'
                          ? 'These proposed tests were made before you changed the kinds of tests.'
                          : 'Content changed since these proposed tests were made.'}
                      </span>
                      <button
                        type="button"
                        disabled={isRetrying}
                        onClick={() => void handleRetry(doc.id)}
                        className="font-semibold text-primary underline hover:no-underline disabled:opacity-60"
                      >
                        {isRetrying ? 'Re-proposing…' : 'Re-propose to update'}
                      </button>
                    </div>
                  )}
                  {/* Inline failure detail — only when the Failed pill
                      is expanded. Shows the reason captured at proposer
                      failure time + a Retry button. Lives inside the
                      same divided row so the visual association with
                      the failing doc is unambiguous. */}
                  {/* A try failed and the next is queued: say why and when,
                      always open, with no Retry (it would only queue a
                      second read). */}
                  {doc.extractionStatus === 'retrying' && (
                    <div className="mt-2 ml-1 rounded border border-amber-500/30 bg-amber-500/10 px-2 py-1.5 text-[10px] text-amber-700 dark:text-amber-400">
                      {retryingLine(doc)}
                    </div>
                  )}
                  {doc.extractionStatus === 'failed' && expanded && (
                    <div className="mt-2 ml-1 rounded border border-destructive/20 bg-destructive/5 px-2 py-1.5">
                      <div className="text-[10px] text-destructive whitespace-pre-line">
                        {failedLine(doc)}
                      </div>
                      <div className="mt-1.5 flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={isRetrying}
                          // A read whose parts are fixed carries on from the
                          // first part it has not read, keeping the rest and
                          // any smaller round size, even before a part is done.
                          onClick={() => void handleRetry(doc.id, canCarryOn(doc) ? { continue: true } : {})}
                          aria-label={partsAlreadyRead(doc) > 0
                            ? `Read the rest of ${doc.filename}`
                            : `Retry extraction for ${doc.filename}`}
                        >
                          <RotateCw className={`h-3 w-3 mr-1 ${isRetrying ? 'animate-spin' : ''}`} />
                          {isRetrying ? 'Retrying…' : partsAlreadyRead(doc) > 0 ? 'Read the rest' : 'Retry'}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* Footer line — visible budget so users see how close they
          are to the cap without having to do mental math. Only
          shown when there are docs (the empty-state already
          surfaces the budget). Shows original-file aggregate
          (MB-scale, intuitive) against the 25 MB cap. */}
      {docs.length > 0 && (
        <div className="text-[10px] text-muted-foreground">
          {docs.length} {docs.length === 1 ? 'file' : 'files'} · {fmtBytes(totalOriginalBytes)} of {fmtBytes(totalOriginalCapBytes)} total
        </div>
      )}

      {error && (
        // `role="alert"` so it is announced when it appears: this panel
        // is populated asynchronously — a failed upload, delete, or (as
        // of #2102) a failed save of the kinds control — and a message
        // that arrives silently is one a screen-reader user never learns
        // about.
        <div
          role="alert"
          className="rounded border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive whitespace-pre-line"
        >
          {error}
        </div>
      )}
    </div>
  )
}
